/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useRef, MutableRef, Ref } from 'preact/hooks';
import { getClientHints } from '../../utils/PRIVATE_clientHints';
import { getIsSelectionPending } from '../../utils/PRIVATE_textSelectionUtils';
import { Keys } from '../../utils/UNSAFE_keys';
import { ColumnResizeInfo, TableColumn } from '../Table';
import { handleRowActionGesture } from '../utils/TableActionUtils';
import {
  handleSelectionGesture,
  handleRangeSelectionGesture,
  isRangeSelectionGesture
} from '../utils/TableSelectionUtils';
import {
  handleOverResizeRegionGesture,
  handleColumnResizeStartGesture,
  handleColumnResizingGesture,
  handleColumnResizeEndGesture,
  handleLeaveResizingGesture,
  handleEnterResizingGesture
} from '../utils/TableSizingUtils';
import { SelectionMode } from '../../UNSAFE_Collection';
import {
  Cell,
  ColumnWidths,
  RowActionDetail,
  ColumnResizeDetail,
  TableSelectionDetail
} from '../../UNSAFE_TableView';

type UsePointerHandlingOptions<K, D, C extends string> = {
  outerTableRef: Ref<HTMLDivElement>;
  tableId: string;
  data: D[] | null;
  isRtl: boolean;
  columnsArray: TableColumn<K, D, C>[];
  appliedColumnWidths: ColumnWidths<C>;
  resizeInfoRef: MutableRef<ColumnResizeInfo<C>>;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  fullColumnWidths?: number[];
  onColumnResizing?: (detail: ColumnResizeDetail<C>) => void;
  onColumnResize?: (detail: ColumnResizeDetail<C>) => void;
  currentCell?: Cell<K, C>;
  selectionMode?: { row?: SelectionMode; column?: SelectionMode };
  selected?: { row?: Keys<K>; column?: Keys<C> };
  onTableSelectionChange?: (
    detail: TableSelectionDetail<K, C>,
    isSelectionExtension: boolean
  ) => void;
  onRowAction?: (detail: RowActionDetail<K, D>) => void;
};

/**
 * Hook that manages pointer interactions on the Preact Table
 */
export function usePointerHandling<K extends string | number, D, C extends string>({
  outerTableRef,
  tableId,
  data,
  isRtl,
  columnsArray,
  appliedColumnWidths,
  resizeInfoRef,
  selectionExtensionElementRef,
  fullColumnWidths,
  onColumnResizing,
  onColumnResize,
  currentCell,
  selectionMode,
  selected,
  onTableSelectionChange,
  onRowAction
}: UsePointerHandlingOptions<K, D, C>) {
  // tracks whether the most recent 'pointerdown' event was due to a touch gesture
  const selectionTouchRef = useRef<boolean>(false);

  // Pointer move handler for the outer Table.
  const _pointerMoveHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null && onColumnResizing != null) {
        if (!resizeInfoRef.current.isResizing) {
          handleOverResizeRegionGesture(
            outerTableRef.current,
            tableId,
            event,
            columnsArray,
            resizeInfoRef,
            isRtl
          );
        } else {
          handleColumnResizingGesture(
            outerTableRef.current,
            tableId,
            event,
            appliedColumnWidths,
            resizeInfoRef,
            isRtl,
            fullColumnWidths,
            onColumnResizing
          );
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      fullColumnWidths,
      onColumnResizing
    ]
  );

  // Pointer down handler for the outer Table. It ensures the area targeted is 'current'.
  // NOTE - This event handling is triggered prior to 'onFocus' handling and ensures the
  // 'pending current' area is set when an initial pointer interaction focuses the Table.
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        selectionTouchRef.current = event.pointerType === 'touch';
        const targetElement = event.target as HTMLElement;
        if (
          onTableSelectionChange != null &&
          currentCell != null &&
          event.shiftKey &&
          isRangeSelectionGesture(
            outerTableRef.current,
            tableId,
            targetElement,
            currentCell,
            selectionMode
          )
        ) {
          // prevent focus change on selection extension gesture
          if (!getIsSelectionPending()) {
            event.preventDefault();
          }
        } else if (
          handleColumnResizeStartGesture(
            outerTableRef.current,
            tableId,
            event,
            columnsArray,
            appliedColumnWidths,
            resizeInfoRef,
            isRtl,
            fullColumnWidths,
            onColumnResizing
          )
        ) {
          // prevent focus change on column resize gesture
          event.preventDefault();
          resizeInfoRef.current.ignoreClick = true;
        }
      }
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      appliedColumnWidths,
      resizeInfoRef,
      isRtl,
      currentCell,
      selectionMode,
      onTableSelectionChange,
      fullColumnWidths,
      onColumnResizing
    ]
  );

  // Pointer up handler for the outer Table.
  const _pointerUpHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleColumnResizeEndGesture(
          outerTableRef.current,
          tableId,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [outerTableRef, tableId, appliedColumnWidths, resizeInfoRef, isRtl, onColumnResize]
  );

  // Pointer leave handler for the outer Table.
  const _pointerLeaveHandler = useCallback(
    (_event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleLeaveResizingGesture(outerTableRef.current, tableId, resizeInfoRef);
      }
    },
    [outerTableRef, tableId, resizeInfoRef]
  );

  // Pointer leave handler for the outer Table.
  const _pointerEnterHandler = useCallback(
    (event: PointerEvent) => {
      if (outerTableRef.current != null) {
        handleEnterResizingGesture(
          outerTableRef.current,
          tableId,
          event,
          appliedColumnWidths,
          resizeInfoRef,
          isRtl,
          onColumnResize
        );
      }
    },
    [outerTableRef, tableId, appliedColumnWidths, resizeInfoRef, isRtl, onColumnResize]
  );

  // Click handler for the outer Table. It ensures the Table's selection state is
  // updated to reflect the area targeted.
  const _clickHandler = useCallback(
    (event: MouseEvent) => {
      let isSelectionExtension = false;
      const platform = getClientHints().platform;
      const target = event.target as HTMLElement;
      if (outerTableRef.current != null) {
        if (
          onTableSelectionChange != null &&
          !resizeInfoRef.current.ignoreClick &&
          !getIsSelectionPending()
        ) {
          if (currentCell != null && event.shiftKey) {
            isSelectionExtension = handleRangeSelectionGesture(
              outerTableRef.current,
              tableId,
              columnsArray,
              target,
              currentCell,
              selectionExtensionElementRef,
              onTableSelectionChange,
              selectionMode,
              selected
            );
          }
          if (!isSelectionExtension) {
            handleSelectionGesture(
              outerTableRef.current,
              tableId,
              columnsArray,
              target,
              (platform === 'mac' ? event.metaKey : event.ctrlKey) || selectionTouchRef.current,
              onTableSelectionChange,
              selectionMode,
              selected
            );
          }
        }
        if (
          onRowAction != null &&
          data != null &&
          !getIsSelectionPending() &&
          handleRowActionGesture(outerTableRef.current, tableId, data, onRowAction, target)
        ) {
          event.stopPropagation();
        }
      }
      resizeInfoRef.current.ignoreClick = false;
    },
    [
      outerTableRef,
      tableId,
      columnsArray,
      data,
      resizeInfoRef,
      selectionExtensionElementRef,
      currentCell,
      selectionMode,
      selected,
      onTableSelectionChange,
      onRowAction
    ]
  );

  return {
    onPointerMove: _pointerMoveHandler,
    onPointerDown: _pointerDownHandler,
    onPointerUp: _pointerUpHandler,
    onPointerLeave: _pointerLeaveHandler,
    onPointerEnter: _pointerEnterHandler,
    onClick: _clickHandler
  };
}
